import { joinDateStringAndTimeString } from '../../../../services/joinDateStringAndTimeString';
import { mustGetPairedWorkCategoryIDByName } from '../../../../services/workCategory';
import { CarAllocation, WorkTime } from '../../../../types/carAllocation';
import {
  addDays,
  addMinutes,
  differenceInMinutes,
  max,
  min,
  set,
} from 'date-fns';

type TaskBase = {
  contractId: number;
  contractDetailId: number;
  workCategory: {
    id: number;
    estimatedTime: number;
    name: string;
  };
  startDate: string;
  startDateForPlacement: string;
  endDate: string;
  endDateForPlacement: string;
  userId: number | undefined;
  carId: number | undefined;
  isLocked: boolean;
  goesStraight?: boolean | null;
  isEditable: {
    vertical: boolean;
    horizontal: boolean;
  };
  beforeCarAllocationIds?: number[];
  afterCarAllocationIds?: number[];
  isMergeTask: boolean;
};
export type TaskAllocated = TaskBase & {
  type: 'allocated';
  carAllocationId: number;
  // 出発・到着は wasteItem が空配列になるため wasteItemId を持たないケースが存在する
  wasteItemId?: number | null;
  isLocked: boolean;
};
type PartialOf<T, RequiredKey extends keyof T> = Partial<T> &
  Pick<T, RequiredKey>;
export type TaskAllocatedInEdit = PartialOf<
  TaskAllocated,
  'type' | 'carAllocationId' | 'workCategory' | 'isLocked' | 'carId' | 'userId'
>;
export type TaskUnallocated = TaskBase & {
  type: 'unallocated';
  // 新規追加には wasteItemId が空になるケースは存在しない
  wasteItemId: number;
};
export type TaskUnallocatedInEdit = PartialOf<TaskUnallocated, 'type'>;
export type AllocationTask = TaskAllocated | TaskUnallocated;
export type AllocationTaskInEdit = TaskAllocatedInEdit | TaskUnallocatedInEdit;
type LineRestTime = {
  start: string;
  end: string;
};
export type Line = {
  order: 'driver' | 'car';
  title: string;
  sub?: string;
  // 表示用: 詳細モーダルを表示するため CarAllocation のまま扱う
  allocations: CarAllocation[];
  restTime?: LineRestTime;
  userId?: number;
  carId?: number;
  isAvailable?: boolean;
};
const isTaskAllocated = (
  allocation: AllocationTask
): allocation is TaskAllocated => {
  return allocation.type === 'allocated';
};
type TimelineConstraint = {
  earlier: Date | null;
  later: Date | null;
};
export const getTimelineConstraint = (
  allocation: AllocationTaskInEdit,
  lineTasks: AllocationTask[]
) => {
  const constraint: TimelineConstraint = {
    earlier: null,
    later: null,
  };
  const tasksAllocated = lineTasks.filter(isTaskAllocated);
  if (tasksAllocated.length === 0 || allocation.type !== 'allocated') {
    return constraint;
  }
  const afterIds = allocation.afterCarAllocationIds;
  if (afterIds && afterIds.length > 0) {
    // 最も早い制約日時を導出したいので比較の初期値は表示している日の翌日
    const originDate = addDays(
      new Date(tasksAllocated[0].startDateForPlacement),
      1
    );
    const earliestConstraint = tasksAllocated
      .filter((a) => afterIds.includes(a.carAllocationId))
      .reduce((latest, allocation) => {
        return min([latest, new Date(allocation.startDateForPlacement)]);
      }, originDate);
    constraint.earlier = earliestConstraint;
  }
  const beforeIds = allocation.beforeCarAllocationIds;
  if (beforeIds && beforeIds.length > 0) {
    // 最も遅い制約日時を導出したいので比較の初期値は表示している日の前日
    const originDate = addDays(
      new Date(tasksAllocated[0].endDateForPlacement),
      -1
    );
    const latestConstraint = tasksAllocated
      .filter((a) => beforeIds.includes(a.carAllocationId))
      .reduce((latest, allocation) => {
        return max([latest, new Date(allocation.endDateForPlacement)]);
      }, originDate);
    constraint.later = latestConstraint;
  }
  return constraint;
};

export const generateAllocationTask = (
  allocations: CarAllocation[],
  driverId: number | undefined,
  carId: number | undefined
) => {
  return allocations.map<AllocationTask>((ca) => {
    const startDate = getAllocationWorkStartDate(ca);
    const endDate = getAllocationWorkEndDate(ca);
    const taskTime = differenceInMinutes(endDate, startDate);
    const startDateForPlacement = snapToHalfHour(startDate);
    const endDateForPlacement = addMinutes(startDateForPlacement, taskTime);
    return {
      type: 'allocated',
      contractId: ca.contractId,
      contractDetailId: ca.contractDetailId,
      carAllocationId: ca.carAllocationId,
      workCategory: {
        ...ca.workCategory,
        estimatedTime: ca.taskEstimatedTime,
      },
      goesStraight: ca.goesStraight,
      wasteItemId: ca.wasteItems.length > 0 ? ca.wasteItems[0].id : null,
      startDate: startDate.toISOString(),
      startDateForPlacement: startDateForPlacement.toISOString(),
      endDate: endDate.toISOString(),
      endDateForPlacement: endDateForPlacement.toISOString(),
      isLocked: ca.isLocked,
      userId: driverId,
      carId: carId,
      isEditable: ca.isEditable,
      beforeCarAllocationIds: ca.beforeCarAllocationIds,
      afterCarAllocationIds: ca.afterCarAllocationIds,
      isMergeTask: ca.isMergeTask,
    };
  });
};

export const snapToHalfHour = (date: Date): Date => {
  if (date.getMinutes() < 30) {
    return set(date, {
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
  } else {
    return set(date, {
      minutes: 30,
      seconds: 0,
      milliseconds: 0,
    });
  }
};

const workTimes = [
  { id: 1, name: '出勤' },
  { id: 2, name: '全休' },
  { id: 3, name: 'AM休' },
  { id: 4, name: 'PM休' },
] as const;
type WorkTimeName = (typeof workTimes)[number]['name'];
const getWorkTimeIdByName = (name: WorkTimeName): number | undefined => {
  return workTimes.find((wt) => wt.name === name)?.id;
};
export const getWorkRestTime = (
  workTime?: WorkTime
): LineRestTime | undefined => {
  if (!workTime || !workTime.workTime) {
    return {
      start: '00:00',
      end: '24:00',
    };
  }

  if (
    [getWorkTimeIdByName('AM休'), getWorkTimeIdByName('PM休')].includes(
      workTime.workTime?.id
    )
  ) {
    return {
      start: workTime.timeFrameStart,
      end: workTime.timeFrameEnd,
    };
  }
  if (workTime.workTime?.id === getWorkTimeIdByName('全休')) {
    return {
      start: '00:00',
      end: '24:00',
    };
  }
  return;
};

export const getAllocationWorkStartDate = (allocation: CarAllocation): Date => {
  const workStartTimeStr = joinDateStringAndTimeString(
    allocation.workStartDate,
    allocation.workStartTime
  );
  if (!workStartTimeStr) {
    throw Error;
  }

  return new Date(workStartTimeStr);
};

export const getAllocationWorkEndDate = (allocation: CarAllocation): Date => {
  const taskEstimatedTime = allocation.taskEstimatedTime;
  const workStartDate = getAllocationWorkStartDate(allocation);

  return addMinutes(workStartDate, taskEstimatedTime);
};

export const isSameTask = (
  source: AllocationTask,
  target: AllocationTask | AllocationTaskInEdit
) => {
  return (
    source.type === target.type &&
    source.contractId === target.contractId &&
    source.contractDetailId === target.contractDetailId &&
    source.workCategory.id === target.workCategory?.id &&
    source.wasteItemId === target.wasteItemId
  );
};

export const isPairTask = (
  source: AllocationTask,
  target: AllocationTask | AllocationTaskInEdit
) => {
  return (
    source.type === target.type &&
    source.contractId === target.contractId &&
    source.contractDetailId === target.contractDetailId &&
    mustGetPairedWorkCategoryIDByName(
      source.workCategory.name,
      source.goesStraight
    ) === target.workCategory?.id &&
    source.wasteItemId === target.wasteItemId
  );
};
